import { apiClient } from '@kss/api';

/**
 * Coupon lookup + discount math, shared by the POS and Sales Invoice bills.
 *
 * Field names match the LIVE /sales/Coupon schema (verified against the API),
 * NOT the older speculative shape: the customer-typed code is `couponCode`,
 * the type is `discountType`, the window is `validFrom`/`validUntil`, and usage
 * is `useCount`/`maxUses` with a `couponStatus` flag.
 *
 * The list endpoint ignores `?couponCode=` filters (gap G78) so we fetch and
 * match client-side. All values are bound from the API row — never hard-coded.
 */

export interface CouponRow {
  id?: number | string;
  couponId?: number | string;
  code?: string;
  name?: string;
  /** The code a customer actually types. */
  couponCode?: string;
  promotionId?: number | string | null;
  /** 'Percentage' | 'Flat' (and possibly FixedAmount/FreeDelivery). */
  discountType?: string;
  discountValue?: number;
  validFrom?: string;
  validUntil?: string;
  maxUses?: number;
  useCount?: number;
  isSingleUsePerCustomer?: boolean;
  couponStatus?: string;
  isActive?: boolean | number;
}

export interface CouponResult {
  ok: boolean;
  coupon?: CouponRow;
  /** Rupee discount to subtract from the order value. */
  discount: number;
  message: string;
}

function listOf(d: unknown): CouponRow[] {
  const b = (d ?? {}) as Record<string, unknown>;
  return (Array.isArray(b) ? b
    : Array.isArray(b.items) ? b.items
    : Array.isArray(b.data) ? b.data
    : Array.isArray((b.data as Record<string, unknown>)?.items) ? (b.data as { items: unknown[] }).items
    : []) as CouponRow[];
}

const codeOf = (c: CouponRow) => String(c.couponCode ?? c.code ?? '').trim().toLowerCase();

/** Find a coupon by the code the customer typed (case-insensitive). */
export async function lookupCoupon(code: string): Promise<CouponRow | null> {
  const trimmed = code.trim().toLowerCase();
  if (!trimmed) return null;
  let rows: CouponRow[] = [];
  try { rows = listOf((await apiClient.get(`/sales/Coupon?couponCode=${encodeURIComponent(code.trim())}`)).data); } catch { /* full list */ }
  if (rows.length === 0) {
    try { rows = listOf((await apiClient.get('/sales/Coupon')).data); } catch { /* none */ }
  }
  return rows.find((c) => codeOf(c) === trimmed) ?? null;
}

/** Validate a coupon against an order value and compute the discount. */
export function applyCoupon(coupon: CouponRow | null, orderValue: number): CouponResult {
  if (!coupon) return { ok: false, discount: 0, message: 'Coupon not found' };
  if (coupon.isActive === false || coupon.isActive === 0) return { ok: false, discount: 0, message: 'Coupon is inactive' };
  if (coupon.couponStatus && !/active/i.test(String(coupon.couponStatus))) {
    return { ok: false, discount: 0, message: `Coupon is ${coupon.couponStatus}` };
  }

  const now = Date.now();
  if (coupon.validFrom) {
    const t = Date.parse(String(coupon.validFrom));
    if (!Number.isNaN(t) && now < t) return { ok: false, discount: 0, message: 'Coupon is not yet valid' };
  }
  if (coupon.validUntil) {
    const t = Date.parse(String(coupon.validUntil));
    if (!Number.isNaN(t) && now > t) return { ok: false, discount: 0, message: 'Coupon has expired' };
  }

  const maxUses = Number(coupon.maxUses ?? 0) || 0;
  const useCount = Number(coupon.useCount ?? 0) || 0;
  if (maxUses > 0 && useCount >= maxUses) {
    return { ok: false, discount: 0, message: 'Coupon usage limit reached' };
  }

  const type = String(coupon.discountType ?? 'Flat');
  const val = Number(coupon.discountValue ?? 0) || 0;
  let discount = 0;
  if (/percent/i.test(type)) {
    discount = (orderValue * val) / 100;
  } else if (/free.?deliv|free.?ship/i.test(type)) {
    return { ok: true, coupon, discount: 0, message: `Free delivery applied (${coupon.couponCode ?? coupon.code})` };
  } else {
    // Flat / FixedAmount / Amount → a rupee value off.
    discount = val;
  }
  discount = Math.min(Math.max(0, discount), orderValue);
  discount = Math.round(discount * 100) / 100;
  if (discount <= 0) return { ok: false, discount: 0, message: 'Coupon yields no discount on this order' };
  return {
    ok: true, coupon, discount,
    message: `Coupon ${coupon.couponCode ?? coupon.code ?? ''} applied — ₹${discount.toLocaleString('en-IN')} off`,
  };
}
